import { useNavigate } from "react-router-dom";
import { useEstablishments } from "@/context/EstablishmentContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Building2, Plus, Pencil, Trash2, FlaskConical, MapPin, Globe } from "lucide-react";
import { EmailBranding } from "@/components/EmailBranding";

export default function Establishments() {
  const { establishments, reload, setCurrent } = useEstablishments();
  const navigate = useNavigate();

  const remove = async (id) => {
    await api.delete(`/establishments/${id}`);
    toast.success("Établissement supprimé");
    await reload();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Mes établissements</h1>
          <p className="text-slate-500 mt-1">Gérez vos établissements. Les données restent strictement séparées.</p>
        </div>
        <Button onClick={() => navigate("/app/establishments/new")} data-testid="add-establishment-button" className="bg-blue-600 hover:bg-blue-700 rounded-xl">
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {establishments.map((e) => (
          <div key={e.id} className="bg-white rounded-2xl p-5 border border-slate-200 hover:shadow-md transition-all duration-200" data-testid={`establishment-card-${e.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 grid place-items-center"><Building2 className="w-5 h-5" /></div>
              {e.is_demo && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold"><FlaskConical className="w-3 h-3" /> DÉMO</span>}
            </div>
            <h3 className="font-heading font-semibold text-lg mt-3">{e.name}</h3>
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {e.city || "—"} · {e.sector}</p>
            {e.website && <p className="text-sm text-slate-400 flex items-center gap-1 mt-0.5 truncate"><Globe className="w-3.5 h-3.5 shrink-0" /> {e.website}</p>}
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => { setCurrent(e.id); navigate("/app/dashboard"); }} data-testid={`open-establishment-${e.id}`} className="rounded-lg flex-1">Ouvrir</Button>
              <Button size="sm" variant="outline" onClick={() => navigate(`/app/establishments/${e.id}/edit`)} data-testid={`edit-establishment-${e.id}`} className="rounded-lg"><Pencil className="w-4 h-4" /></Button>
              {!e.is_demo && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid={`delete-establishment-${e.id}`} className="rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer {e.name} ?</AlertDialogTitle>
                      <AlertDialogDescription>Cette action est irréversible. Tous les audits, tâches et données associés seront supprimés.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(e.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-${e.id}`}>Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
      </div>
      {establishments.filter((e) => !e.is_demo).map((e) => (
        <div key={`brand-${e.id}`} className="mt-6"><p className="text-xs uppercase tracking-wide text-slate-400 mb-2">{e.name}</p><EmailBranding estId={e.id} /></div>
      ))}
    </div>
  );
}
